import React, { useState, useEffect } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import { deposit } from '@ally/data-selectors-afg-accounts'
import { STKDeeplinkScenario, STKDeeplinkScenarios } from '../../types'
import { useHostServices } from '../../HostServices'
import { AccountDetailsSkeletonLoader } from '../../components/common/AccountDetailSkeletonLoader'

/* There isn't much actual logic to test here, and setting up
all of the react-router-dom mocks would be more effort than it's worth */

/* istanbul ignore file */

export const isValidScenario = (
  scenario: STKDeeplinkScenario,
): scenario is STKDeeplinkScenario => {
  return (
    Object.values(STKDeeplinkScenarios) as STKDeeplinkScenario[]
  ).includes(scenario)
}

export const STKRedirector: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const { deeplink, accountId: paramAccountId } =
    useParams<{ deeplink: STKDeeplinkScenario; accountId?: string }>()

  const { hostData } = useHostServices()
  const afgAccountsStatus = hostData.afgAccounts.status.loadState
  const savingAccounts =
    deposit.nonRetirement
      .getSDA(hostData)
      .filter(account => account.status.toLowerCase() === 'active') || []

  useEffect(() => {
    if (afgAccountsStatus === 'loaded') {
      setLoading(false)
    }
  }, [afgAccountsStatus])

  if (loading) {
    return <AccountDetailsSkeletonLoader />
  }

  // no savings accounts
  if (savingAccounts.length === 0) {
    return <Redirect to="/dashboard" />
  }

  // deeplink scenarios
  // ex. /bank/accounts/stk-osa/:accountId/rup
  if (deeplink && deeplink === 'rup') {
    if (!paramAccountId) {
      // account specific deeplinking requires an accountId
      return <Redirect to="/dashboard" />
    }

    return (
      <Redirect
        to={{
          pathname: `/account/${paramAccountId}/details`,
          state: {
            deeplink,
            accountId: paramAccountId,
          },
        }}
      />
    )
  }

  // Handle deeplink from dashboard click
  // ex. /bank/accounts/stk-osa/:accountId/buckets
  // if accountId exists then redirect to the account
  if (deeplink && deeplink === 'buckets' && paramAccountId) {
    return (
      <Redirect
        to={{
          pathname: `/account/${paramAccountId}/details`,
          state: {
            deeplink,
            accountId: paramAccountId,
          },
        }}
      />
    )
  }

  // No accountId in deeplink, done loading -> fetch savings account with highest balance
  const foundAccount = savingAccounts.reduce((accumulator, account) => {
    if (!accumulator) {
      return account
    }

    const prevHighest = accumulator.balance?.available || 0
    const current = account.balance?.available || 0

    return prevHighest < current ? account : accumulator
  })

  // No accountId deeplinks passed in route, use foundAccountId and default deeplink
  const accountId = foundAccount.id

  // deeplink scenarios
  // ex. /bank/accounts/stk-osa or /bank/accounts/stk-osa/boosters or /bank/accounts/stk-osa/buckets
  return (
    <Redirect
      to={{
        pathname: `/account/${accountId}/details`,
        state: {
          deeplink: isValidScenario(deeplink)
            ? deeplink
            : STKDeeplinkScenarios.Default,
          accountId,
        },
      }}
    />
  )
}
